import React, { useState } from 'react';
import axios from 'axios';
import { Search as SearchIcon, X } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import MovieCard from '../components/MovieCard';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Search = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [movies, setMovies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);

  const handleSearch = async (e) => {
    e?.preventDefault();
    if (!searchQuery.trim()) {
      toast.error('Please enter a search term');
      return;
    }

    try {
      setLoading(true);
      setHasSearched(true);
      const response = await axios.get(
        `${API}/movies/search?query=${encodeURIComponent(searchQuery)}&page=1`
      );
      setMovies(response.data.results || []);
    } catch (error) {
      console.error('Error searching movies:', error);
      toast.error('Failed to search movies');
    } finally {
      setLoading(false);
    }
  };

  const handleClear = () => {
    setSearchQuery('');
    setMovies([]);
    setHasSearched(false);
  };

  return (
    <div className="min-h-screen pt-32 px-6 md:px-12 max-w-7xl mx-auto pb-20" data-testid="search-page">
      <div className="mb-12">
        <h1 className="font-anton text-5xl md:text-7xl text-white uppercase tracking-tight mb-4">
          Search Trailers
        </h1>
        <p className="text-base md:text-lg text-gray-400">
          Find any movie trailer from any era
        </p>
      </div>

      {/* Search Bar */}
      <div className="max-w-3xl mx-auto mb-16">
        <form onSubmit={handleSearch} className="relative">
          <div className="relative">
            <SearchIcon className="absolute left-6 top-1/2 -translate-y-1/2 w-6 h-6 text-gray-400" />
            <Input
              type="text"
              placeholder="Search for a movie..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full h-16 pl-16 pr-32 bg-black/60 border-2 border-white/20 focus:border-cinema-red text-white text-lg rounded-xl backdrop-blur-sm"
              data-testid="search-input"
            />
            {searchQuery && (
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="absolute right-24 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white"
                onClick={handleClear}
                data-testid="clear-search-button"
              >
                <X className="w-5 h-5" />
              </Button>
            )}
            <Button
              type="submit"
              className="absolute right-2 top-1/2 -translate-y-1/2 bg-[#7FFF7F] hover:bg-[#6FEF6F] text-black h-12 px-6 rounded-lg shadow-[0_0_15px_rgba(127,255,127,0.4)] hover:shadow-[0_0_25px_rgba(127,255,127,0.6)] transition-all duration-300"
              data-testid="search-button"
            >
              Search
            </Button>
          </div>
        </form>
      </div>

      {/* Results */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="text-center">
            <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-[#7FFF7F] mx-auto mb-4"></div>
            <p className="text-gray-400">Searching...</p>
          </div>
        </div>
      ) : hasSearched ? (
        movies.length > 0 ? (
          <div>
            <p className="text-gray-400 mb-8" data-testid="results-count">
              Found {movies.length} results for "{searchQuery}"
            </p>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-6" data-testid="search-results">
              {movies.map((movie) => (
                <MovieCard key={movie.id} movie={movie} />
              ))}
            </div>
          </div>
        ) : (
          <div className="text-center py-20">
            <p className="text-gray-400 text-lg">No movies found for "{searchQuery}"</p>
            <p className="text-gray-500 mt-2">Try searching with different keywords</p>
          </div>
        )
      ) : (
        <div className="text-center py-20">
          <SearchIcon className="w-16 h-16 text-gray-600 mx-auto mb-4" />
          <p className="text-gray-400 text-lg">Start searching to discover trailers</p>
        </div>
      )}
    </div>
  );
};

export default Search;